import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { openFile, byteSize, Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './driver.reducer';

export const Driver = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const driverList = useAppSelector(state => state.driver.entities);
  const loading = useAppSelector(state => state.driver.loading);
  const totalItems = useAppSelector(state => state.driver.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="driver-heading" data-cy="DriverHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.driver.home.title">Drivers</Translate>
        <div className="d-flex justify-content-end">
          <Button
            className="me-2"
            onClick={handleSyncList}
            disabled={loading}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.driver.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button
            component={Link}
            to="/driver/new"
            variant="contained"
            color="primary"
            id="jh-create-entity"
            data-cy="entityCreateButton"
            startIcon={<FontAwesomeIcon icon="plus" />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.driver.home.createLabel">Create new Driver</Translate>
          </Button>
        </div>
      </h2>
      <div className="table-responsive">
        {driverList && driverList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('name')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.name">Name</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('name')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('enabled')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.enabled">Enabled</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('enabled')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('hiredAt')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.hiredAt">Hired At</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('hiredAt')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('age')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.age">Age</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('age')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('email')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.email">Email</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('email')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('mobile')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.mobile">Mobile</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('mobile')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('defaultImage')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.defaultImage">Default Image</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('defaultImage')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('defaultImageData')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.driver.defaultImageData">Default Image Data</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('defaultImageData')} />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {driverList.map((driver, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/driver/${driver.id}`} variant="text" size="small">
                        {driver.id}
                      </Button>
                    </TableCell>
                    <TableCell>{driver.name}</TableCell>
                    <TableCell>{driver.enabled ? 'true' : 'false'}</TableCell>
                    <TableCell>
                      {driver.hiredAt ? <TextFormat type="date" value={driver.hiredAt} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>{driver.age}</TableCell>
                    <TableCell>{driver.email}</TableCell>
                    <TableCell>{driver.mobile}</TableCell>
                    <TableCell>
                      {driver.createdDate ? <TextFormat type="date" value={driver.createdDate} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>{driver.defaultImage}</TableCell>
                    <TableCell>
                      {driver.defaultImageData ? (
                        <div>
                          {driver.defaultImageDataContentType ? (
                            <a onClick={openFile(driver.defaultImageDataContentType, driver.defaultImageData)}>
                              <img
                                src={`data:${driver.defaultImageDataContentType};base64,${driver.defaultImageData}`}
                                style={{ maxHeight: '30px' }}
                              />
                              &nbsp;
                            </a>
                          ) : null}
                          <span>
                            {driver.defaultImageDataContentType}, {byteSize(driver.defaultImageData)}
                          </span>
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button
                          component={Link}
                          to={`/driver/${driver.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          startIcon={<FontAwesomeIcon icon="pencil-alt" />}
                          data-cy="entityEditButton"
                        >
                        </Button>
                        <Button
                          onClick={() =>
                            (window.location.href = `/driver/${driver.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                          }
                          color="error"
                          startIcon={<FontAwesomeIcon icon="trash" />}
                          data-cy="entityDeleteButton"
                        >
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.driver.home.notFound">No Drivers found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={driverList && driverList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Driver;
